import React, { useRef, useState } from "react";
import { Avatar, CircularProgress, IconButton, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "layouts/applications/wizard/components/FormField";
import { application } from "globals/endpoints";
import { getThumbnailUrl } from "./urlUtils";

// image/webp is deliberately excluded — the backend's JDK ImageIO decoder can't validate it
// (no default WebP reader), so it's rejected server-side; keep the client accept list in sync.
const DEFAULT_ACCEPTED_TYPES = "image/jpeg,image/png,image/gif";
const DEFAULT_MAX_FILE_SIZE_MB = 25;

// Renders the existing "paste a URL" text field unchanged, plus an Upload icon beside it.
// A successful upload auto-fills the same field with the server's served URL (CTLSelectFile
// only ever writes back the local file name, so it can't be reused for this dual-mode field).
function CTLLocationImageUpload({ getValues, formik, column }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const acceptedTypes = column.acceptedTypes || DEFAULT_ACCEPTED_TYPES;
  const maxFileSizeMB = column.maxFileSizeMB || DEFAULT_MAX_FILE_SIZE_MB;
  const value = getValues(column.accessor);

  const setUploadState = (isUploading) => {
    setUploading(isUploading);
    if (column.onUploadStateChange) {
      column.onUploadStateChange(isUploading);
    }
  };

  const handleAddFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) {
      return;
    }

    const allowedTypes = acceptedTypes.split(",").map((type) => type.trim());
    if (!allowedTypes.includes(file.type)) {
      setError("Unsupported file type — please choose an image (JPG, PNG or GIF).");
      return;
    }
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setError(`File is too large — the maximum allowed size is ${maxFileSizeMB} MB.`);
      return;
    }

    setError(null);
    setUploadState(true);

    const formData = new FormData();
    formData.append("file", file);

    application
      .post(column.uploadUrl, formData)
      .then((response) => {
        const { servedUrl, thumbnailUrl } = response.data.data;
        formik.handleChange({
          target: { name: `field_${column.accessor}`, value: servedUrl },
        });
        setThumbnailPreview(thumbnailUrl);
      })
      .catch((err) => {
        setError(
          (err.response && err.response.data && err.response.data.messageDetail) ||
            "Upload failed — please try again."
        );
      })
      .finally(() => {
        setUploadState(false);
      });
  };

  const handleRemove = () => {
    formik.handleChange({
      target: { name: `field_${column.accessor}`, value: "" },
    });
    setThumbnailPreview(null);
    setError(null);
  };

  const previewSrc = thumbnailPreview || getThumbnailUrl(value) || value;

  return (
    <MDBox>
      <MDBox display="flex" alignItems="flex-start" gap={1}>
        <MDBox flexGrow={1}>
          <FormField
            name={`field_${column.accessor}`}
            label={column.Header}
            value={value || ""}
            onChange={formik.handleChange}
            disabled={uploading}
            error={!!error}
          />
        </MDBox>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Tooltip title={value ? "Replace image" : "Upload image"}>
          <span>
            <IconButton color="info" disabled={uploading} onClick={handleAddFileClick} sx={{ mt: 1.5 }}>
              {uploading ? <CircularProgress color="info" size={22} /> : <CloudUploadIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {value && !uploading && (
          <Tooltip title="Remove image">
            <IconButton color="error" onClick={handleRemove} sx={{ mt: 1.5 }}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </MDBox>
      {error && (
        <MDTypography variant="caption" color="error">
          {error}
        </MDTypography>
      )}
      {value && (
        <MDBox mt={1}>
          <Avatar variant="rounded" src={previewSrc} sx={{ width: 64, height: 64 }} />
        </MDBox>
      )}
    </MDBox>
  );
}

export default CTLLocationImageUpload;
